import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { loadAllGoogleFonts } from '../../utils/googleFonts';

export default function BlogPreview() {
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const contentRef = useRef(null);

  // Load all Google Fonts
  useEffect(() => {
    loadAllGoogleFonts();
  }, []);

  // Load content from localStorage
  useEffect(() => {
    const savedContent = localStorage.getItem('blog_preview_content');
    const savedTitle = localStorage.getItem('blog_preview_title');
    
    if (savedContent) {
      setContent(savedContent);
    }
    if (savedTitle) {
      setTitle(savedTitle);
    }
  }, []);

  // Configure DOMPurify to allow all CSS properties
  useEffect(() => {
    DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
      // Allow all style attributes
      if (data.attrName === 'style') {
        // Don't filter any CSS properties
        return;
      }
    });

    return () => {
      DOMPurify.removeAllHooks();
    };
  }, []);

  // Re-add interactive functionality after content is rendered
  useEffect(() => {
    if (contentRef.current && content) {
      // Re-enable embed video interaction
      const embeds = contentRef.current.querySelectorAll('.editor-embed-wrapper');
      embeds.forEach(embed => {
        const overlay = embed.querySelector('.embed-overlay');
        if (overlay) {
          // Remove the overlay in preview mode to allow video interaction
          overlay.remove();
        }
        
        // Re-enable pointer events on iframe and content
        const iframe = embed.querySelector('iframe');
        if (iframe) {
          iframe.style.pointerEvents = 'auto';
          iframe.removeAttribute('tabindex');
        }
        
        // Re-enable pointer events on embed content
        const embedContent = embed.querySelector('.embed-content');
        if (embedContent) {
          embedContent.style.pointerEvents = 'auto';
          // Re-enable pointer events on all children
          embedContent.querySelectorAll('*').forEach(el => {
            el.style.pointerEvents = 'auto';
          });
        }
      });

      // Re-add copy functionality to codeblocks
      const copyButtons = contentRef.current.querySelectorAll('.editor-code-header button');
      copyButtons.forEach(button => {
        const codeBlock = button.closest('.editor-code-block-wrapper');
        if (codeBlock) {
          const codeContent = codeBlock.getAttribute('data-code');
          if (codeContent) {
            button.onclick = (e) => {
              e.preventDefault();
              e.stopPropagation();
              navigator.clipboard.writeText(codeContent).then(() => {
                const originalHTML = button.innerHTML;
                button.innerHTML = 'Copied!';
                button.style.fontSize = '11px';
                setTimeout(() => {
                  button.innerHTML = originalHTML;
                  button.style.fontSize = '';
                }, 2000);
              }).catch(err => console.error('Failed to copy:', err));
            };
          }
        }
      });

      // Re-add expand functionality to wrapped codeblocks
      const expandButtons = contentRef.current.querySelectorAll('.editor-code-block-wrapper button[title*="show full code"]');
      expandButtons.forEach(button => {
        const wrapper = button.closest('.editor-code-block-wrapper');
        if (wrapper) {
          const shade = wrapper.querySelector('div[style*="linear-gradient"]');
          const expandBtnContainer = button.parentElement;
          
          button.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            wrapper.style.maxHeight = wrapper.style.maxHeight === '360px' ? 'none' : '360px';
            if (shade) shade.style.display = wrapper.style.maxHeight === 'none' ? 'none' : 'block';
            if (expandBtnContainer) expandBtnContainer.style.display = wrapper.style.maxHeight === 'none' ? 'none' : 'block';
          };
        }
      });

      // Re-add tooltip functionality
      const tooltipWrappers = contentRef.current.querySelectorAll('.editor-tooltip-wrapper');
      tooltipWrappers.forEach(wrapper => {
        const tooltipText = wrapper.getAttribute('data-tooltip');
        if (tooltipText) {
          // Remove any existing tooltip elements
          const existingTooltip = wrapper.querySelector('.editor-tooltip');
          if (existingTooltip) existingTooltip.remove();
          
          // Create new tooltip element
          const tooltip = document.createElement('span');
          tooltip.className = 'editor-tooltip';
          tooltip.textContent = tooltipText;
          tooltip.style.cssText = `
            position: fixed;
            top: -9999px;
            left: -9999px;
            padding: 0.5em 0.8em;
            opacity: 0;
            pointer-events: none;
            transition: all 0.3s;
            color: #fff;
            background: #105652;
            border-radius: 0.375em;
            font-size: 14px;
            z-index: 1000;
            visibility: hidden;
            max-width: 280px;
            width: max-content;
            white-space: normal;
            word-wrap: break-word;
            text-align: center;
            line-height: 1.4;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          `;
          
          // Create tooltip arrow
          const arrow = document.createElement('span');
          arrow.className = 'editor-tooltip-arrow';
          arrow.style.cssText = `
            position: absolute;
            content: "";
            height: 0.6em;
            width: 0.6em;
            bottom: -0.2em;
            left: 50%;
            transform: translate(-50%, 0) rotate(45deg);
            background: #105652;
          `;
          tooltip.appendChild(arrow);
          
          // Position tooltip function
          const positionTooltip = () => {
            const wrapperRect = wrapper.getBoundingClientRect();
            const tooltipRect = tooltip.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            
            // Calculate ideal position (centered above the text)
            let left = wrapperRect.left + (wrapperRect.width / 2) - (tooltipRect.width / 2);
            let top = wrapperRect.top - tooltipRect.height - 10;
            
            // Adjust horizontal position to prevent overflow
            const padding = 10;
            if (left < padding) {
              left = padding;
            } else if (left + tooltipRect.width > viewportWidth - padding) {
              left = viewportWidth - tooltipRect.width - padding;
            }
            
            // Adjust vertical position if tooltip would go above viewport
            if (top < padding) {
              top = wrapperRect.bottom + 10;
              // Move arrow to top for bottom positioning
              arrow.style.top = '-0.2em';
              arrow.style.bottom = 'auto';
              arrow.style.transform = 'translate(-50%, 0) rotate(45deg)';
            } else {
              // Reset arrow to bottom for top positioning
              arrow.style.top = 'auto';
              arrow.style.bottom = '-0.2em';
              arrow.style.transform = 'translate(-50%, 0) rotate(45deg)';
            }
            
            // Calculate arrow position relative to tooltip
            const arrowLeft = ((wrapperRect.left + wrapperRect.width / 2) - left) / tooltipRect.width * 100;
            arrow.style.left = Math.max(10, Math.min(90, arrowLeft)) + '%';
            
            tooltip.style.left = left + 'px';
            tooltip.style.top = top + 'px';
          };
          
          // Add hover functionality with positioning
          wrapper.onmouseenter = () => {
            // Temporarily show tooltip to measure it
            tooltip.style.visibility = 'visible';
            tooltip.style.opacity = '0';
            document.body.appendChild(tooltip);
            
            // Position the tooltip
            positionTooltip();
            
            // Show tooltip with animation
            tooltip.style.opacity = '1';
            tooltip.style.pointerEvents = 'auto';
          };
          
          wrapper.onmouseleave = () => {
            tooltip.style.opacity = '0';
            tooltip.style.visibility = 'hidden';
            tooltip.style.pointerEvents = 'none';
            // Remove from body after animation
            setTimeout(() => {
              if (tooltip.parentNode === document.body) {
                document.body.removeChild(tooltip);
              }
            }, 300);
          };
          
          // Note: Click editing is only available in editor mode, not preview
          wrapper.style.cursor = 'help'; // Change cursor to indicate tooltip in preview
          
          // Store tooltip element reference on wrapper for cleanup
          wrapper._tooltipElement = tooltip;
        }
      });

      // Initialize all other interactive elements (FAQ, audio, video, etc.)
      // Add similar initialization for other elements as needed
    }
  }, [content]);

  const sanitizedHTML = DOMPurify.sanitize(content, {
    ADD_TAGS: ['iframe', 'video', 'audio', 'source'],
    ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'controls', 'autoplay', 'muted', 'loop', 'preload', 'src', 'type']
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <div className="pt-20 pb-16">
        <div className="container mx-auto px-6 max-w-4xl">
          {/* Title */}
          {title && (
            <h1 className="text-4xl font-bold mb-8 text-center" style={{ color: '#105652' }}>
              {title}
            </h1>
          )}
          
          {/* Preview Badge */}
          <div className="mb-6 text-center">
            <span className="inline-block px-4 py-2 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
              📝 Preview Mode
            </span>
          </div>
          
          {/* Content */}
          <div 
            ref={contentRef}
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
          />
          
          {!content && (
            <div className="text-center py-12">
              <p className="text-gray-500">No content to preview. Please add content in the editor first.</p>
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
}